import { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLazyGetPriceInformationQuery } from '../../api/endpoints/dealerPartsCatalog';
import { useLanguage } from '../../contexts/LanguageContext';

const TopPricePartsReport = () => {
  const { th } = useLanguage();
  const [triggerSearch, { data: priceData, isLoading }] = useLazyGetPriceInformationQuery();
  const [topParts, setTopParts] = useState([]);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    // Sayfa yüklendiğinde veri çek
    triggerSearch({
      partCode: '',
      languageId: 1,
      pageNumber: 1,
      pageSize: 100 // Daha fazla veri çek ki en yüksekleri bulabilelim
    });
  }, [triggerSearch]);

  useEffect(() => {
    if (priceData?.data?.items) {
      // En yüksek retailPrice'a sahip 5 parçayı bul
      const sortedParts = [...priceData.data.items]
        .filter(item => item.retailPrice > 0)
        .sort((a, b) => b.retailPrice - a.retailPrice)
        .slice(0, 5);

      setTopParts(sortedParts);

      // Chart verilerini hazırla
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue('--text-color');
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

      const data = {
        labels: sortedParts.map(item => item.partCode),
        datasets: [
          {
            label: 'Perakende Fiyat (TRY)',
            data: sortedParts.map(item => item.retailPrice),
            backgroundColor: [
              'rgba(255, 99, 132, 0.6)',
              'rgba(54, 162, 235, 0.6)',
              'rgba(255, 206, 86, 0.6)',
              'rgba(75, 192, 192, 0.6)',
              'rgba(153, 102, 255, 0.6)'
            ],
            borderColor: [
              'rgb(255, 99, 132)',
              'rgb(54, 162, 235)',
              'rgb(255, 206, 86)',
              'rgb(75, 192, 192)',
              'rgb(153, 102, 255)'
            ],
            borderWidth: 1
          }
        ]
      };

      const options = {
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        plugins: {
          legend: {
            labels: {
              color: textColor
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat('tr-TR', { 
                    style: 'currency', 
                    currency: 'TRY' 
                  }).format(context.parsed.y);
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: textColorSecondary,
              callback: function(value) {
                return new Intl.NumberFormat('tr-TR', { 
                  style: 'currency', 
                  currency: 'TRY',
                  minimumFractionDigits: 0
                }).format(value);
              }
            },
            grid: {
              color: surfaceBorder
            }
          },
          x: {
            ticks: {
              color: textColorSecondary
            },
            grid: {
              color: surfaceBorder
            }
          }
        }
      };

      setChartData(data);
      setChartOptions(options);
    }
  }, [priceData]);

  const formatCurrency = (amount, currency = 'TRY') => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  const handleRefresh = () => {
    triggerSearch({
      partCode: '',
      languageId: 1,
      pageNumber: 1,
      pageSize: 100
    });
  };

  return (
    <div className="grid">
      <div className="col-12">
        <div className="flex justify-content-between align-items-center mb-4">
          <h2 className="m-0">En Yüksek Fiyatlı 5 Parça Raporu</h2>
          <Button 
            label="Yenile" 
            icon="pi pi-refresh" 
            onClick={handleRefresh}
            loading={isLoading}
            className="p-button-outlined"
          />
        </div>
      </div>

      {/* Grafik */}
      <div className="col-12 lg:col-8">
        <Card title="Fiyat Grafiği">
          {isLoading ? (
            <div className="flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
            </div>
          ) : topParts.length > 0 ? (
            <Chart type="bar" data={chartData} options={chartOptions} style={{ height: '400px' }} />
          ) : (
            <div className="text-center p-4">
              <p>Veri bulunamadı</p>
            </div>
          )}
        </Card>
      </div>

      {/* Özet Kartlar */}
      <div className="col-12 lg:col-4">
        <div className="grid">
          <div className="col-12">
            <Card className="bg-blue-100">
              <div className="flex justify-content-between align-items-center">
                <div>
                  <div className="text-500 font-medium mb-2">Toplam Parça</div>
                  <div className="text-900 font-bold text-xl">{topParts.length}</div>
                </div>
                <div className="flex align-items-center justify-content-center bg-blue-500 border-round" 
                     style={{ width: '3rem', height: '3rem' }}>
                  <i className="pi pi-box text-white text-xl"></i>
                </div>
              </div>
            </Card>
          </div>

          <div className="col-12">
            <Card className="bg-green-100">
              <div className="flex justify-content-between align-items-center">
                <div>
                  <div className="text-500 font-medium mb-2">En Yüksek Fiyat</div>
                  <div className="text-900 font-bold text-xl">
                    {topParts.length > 0 ? formatCurrency(topParts[0]?.retailPrice) : '-'}
                  </div>
                </div>
                <div className="flex align-items-center justify-content-center bg-green-500 border-round" 
                     style={{ width: '3rem', height: '3rem' }}>
                  <i className="pi pi-arrow-up text-white text-xl"></i>
                </div>
              </div>
            </Card>
          </div>

          <div className="col-12">
            <Card className="bg-orange-100">
              <div className="flex justify-content-between align-items-center">
                <div>
                  <div className="text-500 font-medium mb-2">Ortalama Fiyat</div>
                  <div className="text-900 font-bold text-xl">
                    {topParts.length > 0 
                      ? formatCurrency(topParts.reduce((sum, item) => sum + item.retailPrice, 0) / topParts.length)
                      : '-'
                    }
                  </div>
                </div>
                <div className="flex align-items-center justify-content-center bg-orange-500 border-round" 
                     style={{ width: '3rem', height: '3rem' }}>
                  <i className="pi pi-chart-line text-white text-xl"></i>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Detay Tablosu */}
      <div className="col-12">
        <Card title="Detaylı Liste">
          <DataTable 
            value={topParts} 
            loading={isLoading}
            emptyMessage="Veri bulunamadı"
            stripedRows
            showGridlines
          >
            <Column 
              field="partCode" 
              header="Parça Kodu" 
              style={{ width: '15%' }}
            />
            <Column 
              field="partName" 
              header="Parça Adı" 
              style={{ width: '30%' }}
            />
            <Column 
              field="brand" 
              header="Marka" 
              style={{ width: '15%' }}
            />
            <Column 
              field="retailPrice" 
              header="Perakende Fiyat" 
              body={(rowData) => formatCurrency(rowData.retailPrice, rowData.currency)}
              style={{ width: '15%' }}
            />
            <Column 
              field="discountedPrice" 
              header="İndirimli Fiyat" 
              body={(rowData) => formatCurrency(rowData.discountedPrice, rowData.currency)}
              style={{ width: '15%' }}
            />
            <Column 
              field="discountRate" 
              header="İndirim Oranı" 
              body={(rowData) => rowData.discountRate ? `${rowData.discountRate}%` : '-'}
              style={{ width: '10%' }}
            />
          </DataTable>
        </Card>
      </div>
    </div>
  );
};

export default TopPricePartsReport;
